import { parseDate, today } from "@internationalized/date";
import { useMemo, useState, type ReactNode } from "react";
import { MerrySegmentedSwitch } from "@/shared/ui/MerrySegmentedSwitch";
import reportIcon from "@/assets/figma/store-overview/report.svg";
import temporaryChatIcon from "@/assets/figma/store-overview/new-chat.svg";
import closeOverviewIcon from "@/assets/figma/store-overview/close.svg";
import {
	formatStoreOverviewAsOf,
	isValidCustomRange,
	MAX_CUSTOM_RANGE_DAYS,
	storeOverviewMissingReason,
} from "@/features/store-overview/storeOverviewPresentation";
import type { StoreOverview } from "@/features/store-overview/storeOverviewSchemas";
import type { StoreOverviewRangeKind } from "@/features/desktop-shell/desktopShellData";
import {
	type MerryDateRange,
} from "@/shared/ui/MerryDatePicker";
import { MerryDatePickerDropdown } from "@/shared/ui/MerryDatePickerDropdown";
import { OverviewPanel } from "@/features/store-overview/dashboard/overview-panel";
import { toOverviewPanelData } from "@/features/store-overview/dashboard/overview-panel-data";

interface StoreOverviewPanelProps {
	customEnd: string;
	customStart: string;
	isCreatingChat: boolean;
	isCreatingReport: boolean;
	isTemporaryChatCollapsed: boolean;
	onClose: () => void;
	onCreateReport: () => void;
	onOpenTemporaryChat: () => void;
	onRangeChange: (
		range: StoreOverviewRangeKind,
		customStart?: string,
		customEnd?: string,
	) => void;
	overview: StoreOverview;
	range: StoreOverviewRangeKind;
	reportNotice: string;
}

const rangeLabels: ReadonlyArray<{
	key: StoreOverviewRangeKind;
	label: string;
}> = [
	{ key: "today", label: "今天" },
	{ key: "7d", label: "7天" },
	{ key: "30d", label: "30天" },
	{ key: "custom", label: "自定义" },
];

function safeDate(value: string) {
	try {
		return value ? parseDate(value) : null;
	} catch {
		return null;
	}
}

function OverviewToast({ children, className }: { children: ReactNode; className: string }) {
	const [visible, setVisible] = useState(true);
	return visible ? (
		<div className={className} role="status" onAnimationEnd={() => setVisible(false)}>
			{children}
		</div>
	) : null;
}

export function StoreOverviewPanel({
	customEnd,
	customStart,
	isCreatingChat,
	isCreatingReport,
	isTemporaryChatCollapsed,
	onClose,
	onCreateReport,
	onOpenTemporaryChat,
	onRangeChange,
	overview,
	range,
	reportNotice,
}: StoreOverviewPanelProps) {
	const defaultEnd = useMemo(
		() => today(overview.queryScope.reportingTimezone),
		[overview.queryScope.reportingTimezone],
	);
	const defaultStart = useMemo(
		() => defaultEnd.subtract({ days: 6 }),
		[defaultEnd],
	);
	const canonicalRange = useMemo<MerryDateRange>(
		() => ({
			end: safeDate(customEnd) ?? defaultEnd,
			start: safeDate(customStart) ?? defaultStart,
		}),
		[customEnd, customStart, defaultEnd, defaultStart],
	);
	const [rangeError, setRangeError] = useState("");
	const [isDateDialogOpen, setDateDialogOpen] = useState(false);

	const isEmpty = overview.coverage.state === "empty";
	const isPartial = overview.coverage.state === "partial";
	const isStale = overview.coverage.reasonCodes.some(
		(reason) => reason === "source_stale" || reason === "source_snapshot_stale",
	);

	const updateCustomRange = (nextRange: MerryDateRange) => {
		const start = nextRange.start.toString();
		const end = nextRange.end.toString();
		if (!isValidCustomRange(start, end)) {
			setRangeError(`自定义范围最多 ${MAX_CUSTOM_RANGE_DAYS} 个自然日`);
			return;
		}
		setRangeError("");
		onRangeChange("custom", start, end);
		setDateDialogOpen(false);
	};

	return (
		<section
			aria-labelledby="store-overview-title"
			className="store-overview-panel"
			data-coverage-state={overview.coverage.state}
			data-figma-node-id="736:110402"
			data-testid="store-overview-panel"
		>
			<header className="flex flex-wrap items-center justify-between gap-2 px-5 pt-4 pb-3">
				<div className="flex items-baseline gap-2">
					<h1
						id="store-overview-title"
						tabIndex={-1}
						className="text-sm font-semibold text-slate-900"
					>
						全店数据总览
					</h1>
					<span className="text-[10px] text-slate-400">
						· 数据截至 {formatStoreOverviewAsOf(overview)}
					</span>
				</div>
				<div className="store-overview-panel__header-actions">
					<MerrySegmentedSwitch size={34} className="overview-segmented"
						aria-label="时间范围"
						options={rangeLabels.map(({ key, label }) => ({ value: key, label }))}
						value={range}
							onChange={(nextRange) => { if (nextRange !== "custom") onRangeChange(nextRange); }}
                        renderOption={(option, button) => option.value === "custom" ? (
                            <MerryDatePickerDropdown
                                aria-label="选择全店总览日期范围"
                                closeOnSelect={false}
                                isOpen={isDateDialogOpen}
                                maxValue={defaultEnd}
                                mode="range"
                                onChange={updateCustomRange}
                                onOpenChange={setDateDialogOpen}
                                placement="bottom end"
                                timeZone={overview.queryScope.reportingTimezone}
                                value={canonicalRange}
                            >
                                {button}
                            </MerryDatePickerDropdown>
                        ) : button}
					/>
					<button
						type="button"
						className="inline-flex items-center gap-1 rounded-full bg-slate-900/[0.05] px-2.5 py-1 text-[11px] font-medium text-slate-800 hover:bg-slate-900/[0.09]"
						disabled={!overview.actions.canExportReport || isCreatingReport || isEmpty}
						onClick={onCreateReport}
					>
						<img alt="" aria-hidden="true" src={reportIcon} />
						{isCreatingReport ? "创建中" : "存为报告"}
					</button>
					<button
						type="button"
						className="inline-flex items-center gap-1 rounded-full bg-slate-900/[0.05] px-2.5 py-1 text-[11px] font-medium text-slate-800 hover:bg-slate-900/[0.09]"
						disabled={!overview.actions.canCreateTemporaryConversation || isCreatingChat || isEmpty}
						onClick={onOpenTemporaryChat}
						aria-label={isTemporaryChatCollapsed ? "展开临时聊天" : "打开临时聊天"}
					>
						<img alt="" aria-hidden="true" src={temporaryChatIcon} />
						{isCreatingChat ? "创建中" : isTemporaryChatCollapsed ? "展开聊天" : "临时聊天"}
					</button>
					<button
						type="button"
						onClick={onClose}
						aria-label="关闭数据总览"
						className="rounded-full p-1 text-slate-600 hover:bg-slate-900/[0.06]"
					>
						<img alt="" aria-hidden="true" src={closeOverviewIcon} />
					</button>
				</div>
			</header>
            {rangeError ? <p className="px-5 text-sm text-red-600" role="alert">{rangeError}</p> : null}

			<div className="store-overview-panel__notices">
			{isPartial || isStale ? (
				<OverviewToast key={`${overview.coverage.state}-${overview.coverage.reasonCodes.join(",")}`} className="store-overview-coverage">
					<strong>{isStale ? "部分数据已过期" : "部分数据尚未覆盖"}</strong>
					<span>
						已覆盖 {overview.coverage.sources.covered}/
						{overview.coverage.sources.expected} 个来源；未知值不会按 0 计算。
					</span>
					{overview.coverage.reasonCodes.length > 0 ? (
						<small>
							{overview.coverage.reasonCodes
								.map(storeOverviewMissingReason)
								.join(" · ")}
						</small>
					) : null}
				</OverviewToast>
			) : null}

			{reportNotice ? (
				<OverviewToast key={reportNotice} className="store-overview-report-notice">
					{reportNotice}
				</OverviewToast>
			) : null}
			</div>

			{isEmpty ? (
				<div className="store-overview-empty" role="status">
					<strong>暂无可验证的全店经营快照</strong>
					<p>
						绑定店铺并等待服务端采集完成后，这里会展示数据；当前不会用 0
						代替未知值。
					</p>
				</div>
			) : (
				<OverviewPanel data={toOverviewPanelData(overview)} />
			)}
		</section>
	);
}
